import { COMPANY_TZ } from "@marble/core/modules/timezones/constants";
import { DateTime } from "luxon";

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * True when `date` is within `hours` hours from now (in either direction —
 * this checks the difference, not just future dates). `bufferSeconds` widens
 * the window, e.g. to account for clock skew.
 */
export const isNHoursOrLessFromNow = (
  date: Date,
  hours: number,
  bufferSeconds: number = 0,
) => {
  return date.getTime() - Date.now() < hours * ONE_HOUR_MS + bufferSeconds;
};

/**
 * Adds (or subtracts, for a negative `days`) business days to `date`,
 * skipping weekends, in `timezone` (the company timezone by default).
 *
 * In `@marble/core` because business-day math anchors every task and case SLA,
 * and those tasks are created from both `apps/web` and `workers/lambdas`.
 */
export const addOrSubtractBusinessDays = (
  date: Date,
  days: number,
  atTime?: { hour: number; minute: number },
  /**
   * When true, adjusts the final result to include “weekend” days
   * if the final result falls on a Friday or Monday ("sentinel weekdays").
   *
   * This is useful for “within N business days” windows where weekend
   * appointments should be treated as belonging to the preceding (or following)
   * business-day cutoff:
   * - **Addition**: if the computed date lands on **Friday**, push returned date to **Sunday**
   *   (adds 2 calendar days).
   * - **Subtraction**: if the computed date lands on **Monday**, push returned date to
   *   **Saturday** (subtracts 2 calendar days).
   */
  pushSentinelWeekdaysToSundays?: boolean,
  timezone: string = COMPANY_TZ,
): Date => {
  let dt = DateTime.fromJSDate(date, { zone: timezone });
  if (atTime) {
    dt = dt.set({
      hour: atTime.hour,
      minute: atTime.minute,
      second: 0,
      millisecond: 0,
    });
  }

  const isSubtracting = days < 0;
  const absDays = Math.abs(days);
  let count = 0;

  while (count < absDays) {
    dt = isSubtracting ? dt.minus({ days: 1 }) : dt.plus({ days: 1 });
    // Skip weekends (6 = Saturday, 7 = Sunday in Luxon)
    if (dt.weekday < 6) {
      count++;
    }
  }
  return _pushSentinelWeekdaysToWeekend(
    dt,
    days,
    pushSentinelWeekdaysToSundays,
  ).toJSDate();
};

/* Returns a datetime corresponding to the previous business day if before 8 AM in NYC time, otherwise returns the date as is. */
export const pushDateToLastBusinessDay = (date: Date): Date => {
  const nycNow = DateTime.fromJSDate(date, { zone: COMPANY_TZ });
  const eightAmToday = nycNow.startOf("day").set({
    hour: 8,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  const pushedDate =
    nycNow < eightAmToday ? addOrSubtractBusinessDays(date, -1) : date;
  return pushedDate;
};

/**
 * Returns the number of business days between two dates forward or backward.
 * Counts all weekday calendar days from `from` to `to`, excluding weekends.
 * Positive if `to` is after `from`, negative if before.
 *
 * @example Dec 12, 2025 → Dec 25, 2025 = +9 business days
 * @example Dec 25, 2025 → Dec 12, 2025 = -9 business days
 */
export const countBusinessDaysDiff = (
  from: Date,
  to: Date,
  timezone: string = COMPANY_TZ,
): number => {
  const fromDt = DateTime.fromJSDate(from, { zone: timezone }).startOf("day");
  const toDt = DateTime.fromJSDate(to, { zone: timezone }).startOf("day");
  const isForward = toDt > fromDt;
  let current = fromDt;
  let count = 0;

  while (!current.hasSame(toDt, "day")) {
    current = isForward
      ? current.plus({ days: 1 })
      : current.minus({ days: 1 });
    if (current.weekday < 6) {
      count++;
    }
  }
  return isForward ? count : -count;
};

const _pushSentinelWeekdaysToWeekend = (
  dt: DateTime,
  days: number,
  enabled?: boolean,
): DateTime => {
  if (!enabled || days === 0) {
    return dt;
  }

  // Luxon weekday: Monday=1 ... Sunday=7
  if (days > 0 && dt.weekday === 5) {
    return dt.plus({ days: 2 }); // Friday -> Sunday
  }

  if (days < 0 && dt.weekday === 1) {
    return dt.minus({ days: 2 }); // Monday -> Saturday
  }

  return dt;
};
